import { GQL_ID } from '@@types'
import classNames from 'classnames'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { ReactSVG } from 'react-svg'
import { useMediaQuery } from 'usehooks-ts'

import { useCartInfoStore, useCartStore } from '@entities/Cart'

import { useAuth } from '@features/auth'
import { useModalStore } from '@features/modal'
import { useManagePaymentMethods } from '@features/payment/hooks/useManagePaymentMethods'
import { useAddressStore } from '@features/shipping-methods'

import PaymentModal from '@widgets/payment-modal/ui/PaymentModal'

import { URL_CART, URL_PAGE_CATALOG } from '@shared/contants'
import useSBPBanks from '@shared/hooks/useSBPBanks'
import { useAnalytics } from '@shared/lib/analytics'
import { computeCartTotalsBreakdown } from '@shared/lib/cartTotals'
import { useLocalizedNavigate as useNavigate } from '@shared/lib/locale'
import Button from '@shared/ui/button'
import DotsLoader from '@shared/ui/dots-loader'
import Wrapper from '@shared/ui/wrapper'
import { formatPrice } from '@shared/uttils'
import { formatGramsToHumanWeight } from '@shared/uttils/formatGramsToHumanWeight'
import getNoun from '@shared/uttils/getNoun'

import styles from './styles.module.scss'

interface OrderAmountProps {
  onSubmit: (paymentId: GQL_ID) => void
  loading: boolean
  disabled?: boolean
  buttonText?: string
  onPaymentMethodChange?: (paymentId: GQL_ID, pluginId?: string) => void
}

export default function OrderAmount ({
  onSubmit,
  loading,
  disabled = false,
  buttonText,
  onPaymentMethodChange,
}: OrderAmountProps) {
  const navigate = useNavigate()
  const [ checkoutButtonLoading, setCheckoutButtonLoading ] =
    useState<boolean>(false)
  const isMobile = useMediaQuery('(max-width: 768px)')
  const { t: tp } = useTranslation('product')
  const { t: to } = useTranslation('order')
  const { t: tc } = useTranslation('common')
  const { t: tca } = useTranslation('cart')
  const { t: tn } = useTranslation('navigation')
  const {
    getTotalQuantity,
    checkProductsInStock,
    removeZeroAmountItemsInCurrentCart,
    syncCart,
  } = useCartStore()

  const isPickup = useAddressStore((state) => state.isPickup)

  const cartTotal = useCartInfoStore((state) => state.cartInfo)
  const cartLoading = useCartInfoStore((state) => state.cartInfoLoading)

  // Используем правильный расчёт тоталов
  const {
    deliveryTotal,
    deliverySale,
    orderTotal,
    bonusSale,
    couponSale,
    loyaltyDiscount,
    hasSalesBreakdown,
  } = useMemo(
    () => computeCartTotalsBreakdown(cartTotal, isPickup()),
    [ cartTotal, isPickup ]
  )

  // Стоимость товаров до скидок (baseTotal)
  const productsTotal = cartTotal?.totals?.baseTotal ?? 0
  // Скидка на товары (без бонусов/купонов/лояльности)
  const productDiscount = cartTotal?.totals?.baseSale ?? 0

  const { openModal } = useModalStore()
  const [ selected, setSelected ] = useState<GQL_ID | null>(null)
  const { paymentMethods, defaultCard } = useManagePaymentMethods()
  const { isAuthorized } = useAuth()
  const { addPaymentInfo } = useAnalytics()
  const paymentTrackedRef = useRef<GQL_ID | null>(null)

  useEffect(() => {
    if (paymentMethods.length === 0) {
      setSelected('new-card')
    }
    else if (defaultCard?.id) {
      setSelected(String(defaultCard.id))
    }
  }, [ defaultCard, paymentMethods ])

  // Track add_payment_info when payment method is selected
  useEffect(() => {
    const products = cartTotal?.products
    if (selected && products && products.length > 0 && paymentTrackedRef.current !== selected) {
      paymentTrackedRef.current = selected
      // Determine payment type
      let paymentType = 'card'
      if (selected === 'new-sbp' || paymentMethods.find(m => m?.id === selected)?.type === 'sbp') {
        paymentType = 'sbp'
      }
      addPaymentInfo(products, paymentType)
    }
  }, [ selected, cartTotal?.products, paymentMethods ])

  const selectedCard = useMemo(() => {
    return paymentMethods?.find(
      (method) => method?.type === 'card' && method.id === selected,
    )
  }, [ paymentMethods, selected ])

  const selectedSBP = useMemo(() => {
    return paymentMethods?.find(
      (method) => method?.type === 'sbp' && method.id === selected,
    )
  }, [ paymentMethods, selected ])

  const { staticBanks } = useSBPBanks()

  // Находим банк для выбранного СБП метода
  const selectedBank = useMemo(() => {
    if (!selectedSBP?.last) return null
    return staticBanks.find((b) => b.schema === `bank${selectedSBP.last}`)
  }, [ selectedSBP, staticBanks ])

  const cardIcons: Record<string, string> = {
    master: 'payments/mastercard',
    visa: 'payments/visa',
  }

  // Определяем иконку в зависимости от выбранного метода
  let iconSrc = `/icons/${cardIcons[selectedCard?.card ?? ''] ?? 'card'}.svg`
  let useImgTag = false
  
  if (selected === 'new-sbp') {
    iconSrc = '/icons/payments/sbp.svg'
    useImgTag = true
  }
  else if (selectedSBP) {
    if (selectedBank) {
      iconSrc = selectedBank.logo
      useImgTag = true
    }
    else {
      iconSrc = '/icons/payments/sbp.svg'
      useImgTag = true
    }
  }

  const TOTAL = getTotalQuantity()

  const handleClick = async () => {
    setCheckoutButtonLoading(true)

    const hasChanges = await checkProductsInStock()
    await removeZeroAmountItemsInCurrentCart(isAuthorized)
    await syncCart()

    setCheckoutButtonLoading(false)

    if (hasChanges) {
      navigate(URL_CART)
    }
    else {
      onSubmit(selected ?? 'new-card')
    }
  }

  return !loading || !cartLoading ? (
    <div className={styles.root}>
      <Wrapper className={styles.root__wrapper}>
        {cartTotal?.products?.length ? (
          <>
            <div className={styles.root__top}>
              <h3>
                {TOTAL}{' '}
                {getNoun(
                  TOTAL as number,
                  tp('product.1'),
                  tp('product.2'),
                  tp('product.3'),
                )}
              </h3>
              <p>
                ~{' '}
                {formatGramsToHumanWeight({
                  weight: Number(cartTotal?.totalWeight),
                  t: tc,
                })}
              </p>
            </div>
            <div className={styles.root__body}>
              {/* === НАД РАЗДЕЛИТЕЛЕМ: Товары + скидка + доставка === */}
              
              {/* Товары */}
              <div className={styles.root__item}>
                <p>{tca('total.products')}:</p>
                <span>{formatPrice(productsTotal)}</span>
              </div>

              {/* Скидка на товары */}
              {productDiscount > 0 && (
                <div className={styles.root__item}>
                  <p>{tca('total.discountOnProducts')}:</p>
                  <span>− {formatPrice(productDiscount)}</span>
                </div>
              )}

              {/* Доставка (не показываем для самовывоза) */}
              {!isPickup() && deliveryTotal > 0 && (
                <div className={styles.root__item}>
                  <p>{tca('total.delivery')}:</p>
                  <span>{formatPrice(deliveryTotal - deliverySale)}</span>
                </div>
              )}

              <div className={styles.root__line} />

              {/* === ПОД РАЗДЕЛИТЕЛЕМ: Скидки на заказ + промокоды + итого === */}

              {/* Детализация скидок на заказ */}
              {hasSalesBreakdown && (
                <>
                  {/* Списано бонусов */}
                  {bonusSale > 0 && (
                    <div className={styles.root__item}>
                      <p>{tca('total.bonusCharge')}:</p>
                      <span>− {formatPrice(bonusSale)}</span>
                    </div>
                  )}
                  {/* Скидка по программе лояльности */}
                  {loyaltyDiscount > 0 && (
                    <div className={styles.root__item}>
                      <p>{tca('total.loyaltyDiscount')}:</p>
                      <span>− {formatPrice(loyaltyDiscount)}</span>
                    </div>
                  )}
                </>
              )}

              {/* Промокод/купон */}
              {couponSale > 0 && (
                <div className={styles.root__item}>
                  <p>{tca('total.couponUsed')}:</p>
                  <span>− {formatPrice(couponSale)}</span>
                </div>
              )}

              {/* Скидка на доставку */}
              {!isPickup() && deliverySale > 0 && (
                <div className={styles.root__item}>
                  <p>{tca('total.discount')}:</p>
                  <span>− {formatPrice(deliverySale)}</span>
                </div>
              )}

              {/* Итого */}
              <div
                className={classNames(
                  styles.root__item,
                  styles['root__item--total'],
                )}
              >
                <p>{tca('total.total')}:</p>
                <span>{formatPrice(orderTotal)}</span>
              </div>
            </div>
          </>
        ) : (
          <div className={styles.root__empty}>
            {tca('empty_cart')}
            <Button to={URL_PAGE_CATALOG}>{tn('go_to_catalog')}</Button>
          </div>
        )}
        <Button
          onClick={() => {
            openModal(
              'orderPayments',
              <PaymentModal
                onChoose={(cardId: GQL_ID) => {
                  setSelected(cardId)
                  // Для существующих методов вызываем callback для обновления заказа
                  if (cardId !== 'new-card' && cardId !== 'new-sbp' && onPaymentMethodChange) {
                    const method = paymentMethods.find(m => m?.id === cardId)
                    if (method) {
                      onPaymentMethodChange(cardId, method.pluginId)
                    }
                  }
                }}
                current={selected}
              />,
              {
                title: to('order.payments-add'),
              },
            )
          }}
          size={!isMobile ? 'large' : 'medium'}
          className={styles.root__button}
          theme="grey"
        >
          {useImgTag ? (
            <img src={iconSrc} alt="" className={styles.root__bankIcon} />
          ) : (
            <ReactSVG src={iconSrc} />
          )}
          {selected === 'new-card'
            ? to('new_card_payment')
            : selected === 'new-sbp'
              ? to('new_sbp_payment')
              : selectedCard
                ? `${to('card')} ****${selectedCard.last}`
                : selectedSBP
                  ? selectedBank?.name || to('sbp_payment')
                  : to('new_card_payment')}
          <ReactSVG src="/icons/arrow-right.svg" />
        </Button>
        <Button
          type="submit"
          disabled={!cartTotal?.products?.length || loading || disabled}
          onClick={handleClick}
          size={!isMobile ? 'large' : 'medium'}
          className={styles.root__button}
        >
          {checkoutButtonLoading ? (
            <DotsLoader />
          ) : (
            buttonText || to('checkout_button')
          )}
        </Button>
      </Wrapper>
    </div>
  ) : (
    <SkeletonTheme>
      <Skeleton
        count={1}
        width={'100%'}
        height={300}
        style={{
          borderRadius: 32,
        }}
      />
    </SkeletonTheme>
  )
}
